import type { FC, ReactNode } from 'react'

import { ArrowDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatNumber, formatPercent } from '@/lib/format'

export interface FunnelStep {
    label: string
    description: string
    value: number
}

interface FunnelChartProps {
    steps: FunnelStep[]
    color?: string
    className?: string
}

// Conversion funnel: each bar is relative to the first step, with the step to step rate between bars
export const FunnelChart: FC<FunnelChartProps> = ({ steps, color = 'var(--chart-2)', className }): ReactNode => {
    const first = steps[0]?.value ?? 0

    return (
        <ol className={cn('space-y-1', className)}>
            {steps.map((step, index) => {
                const width = first === 0 ? 0 : Math.max(1.5, (step.value / first) * 100)
                const previous = index > 0 ? steps[index - 1].value : null
                const rate = previous ? (step.value / previous) * 100 : null

                return (
                    <li key={step.label}>
                        {rate !== null && (
                            <div className='flex items-center gap-1.5 py-1.5 pl-1 text-xs text-muted-foreground'>
                                <ArrowDown className='size-3.5' aria-hidden='true' />
                                <span>
                                    <span className='font-semibold text-foreground tabular-nums'>{formatPercent(rate, rate < 10 ? 1 : 0)}</span> lanjut ke tahap ini
                                </span>
                            </div>
                        )}

                        <div className='flex items-baseline justify-between gap-3'>
                            <span className='min-w-0'>
                                <span className='block text-sm font-medium text-foreground'>
                                    {step.label}
                                </span>
                                <span className='block truncate text-xs text-muted-foreground'>
                                    {step.description}
                                </span>
                            </span>

                            <span className='shrink-0 text-sm font-semibold text-foreground tabular-nums'>
                                {formatNumber(step.value)}
                            </span>
                        </div>

                        <div className='mt-2 h-3 overflow-hidden rounded-r-[4px] bg-muted/80' aria-hidden='true'>
                            <div className='h-full rounded-r-[4px] transition-[width] duration-500' style={{ width: `${width}%`, background: color }} />
                        </div>
                    </li>
                )
            })}
        </ol>
    )
}
